import React from 'react';
import {Image, ImageStyle, Platform, StyleSheet, View, ViewStyle} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {BottomTabHeaderProps} from '@react-navigation/bottom-tabs';
import {DrawerActions} from '@react-navigation/native';
import {StackHeaderProps} from '@react-navigation/stack';
import {ThemeProvider} from '@rneui/themed';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';

import {config as appConfig} from '../config';
import {useOptionalSearchDispatch} from '../context/SearchContext';
import {handleLogoutPress} from '../helpers/authHelpers';
import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import logoImg from '../images/brand/header-logo.png';
import FilterIconImg from '../images/brand/svg/icons/Filter.svg';
import ShopStoreImg from '../images/brand/svg/icons/My_store_icon_2.svg';
import AccountImg from '../images/svg/icons/HeaderAccount.svg';
import {AppRoute} from '../models/AppRoute';
import {Department} from '../models/middleware';
import {HeaderConfig, InformationContentType, ShopAllParams} from '../models/navigationTypes';
import * as RootNavigation from '../navigation/RootNavigation';
import {isViewAll, viewAllSubDepartment} from '../navigation/viewAllSubDepartment';
import WebDesktopTopNavigation from '../navigation/WebDesktopTopNavigation';
import {useGlobalAlert} from '../queries/homepage/useGlobalAlert';
import {navigateToGiftCardsPromotionOrLoadingScreen} from '../queries/promotions/navigateToGiftCardsPromotion';
import {readPromotions} from '../queries/promotions/promotionsAccess';
import {
  ABOUT_US,
  ACCOUNT_ACTION_CHOOSER,
  ACCOUNT_COMMUNICATIONS_PREFERENCES,
  BRAND_NAME,
  CHANGE_PASSWORD,
  CONTACT_US,
  CONTACT_US_SUBMIT_TICKET,
  DELETE_ACCOUNT,
  DELIVERY_AND_PICKUP_FAQS,
  DEPARTMENT_NAME,
  DISCOVER_FILTER,
  DISCOVER_SEGMENT,
  EDIT_ACCOUNT,
  EXCLUSIVE_OFFER_LIST,
  EXCLUSIVE_OFFER_NAME,
  FAVOURITE_BRANDS,
  FEED_SEGMENT,
  GIFT_CARDS_HOW_TO_CHECK_BALANCE,
  GIFT_CARDS_HOW_TO_REDEEM,
  GIFT_CARDS_OPTIONS,
  GIFT_CARDS_STORE_ORDER_REVIEW,
  GIFT_CARDS_TERMS_AND_CONDITIONS,
  INFORMATION,
  INFORMATION_CONTENT,
  LOGIN,
  LOGIN_ENTRY,
  MEMBERS_PRICE_LIST,
  NEW_ACCOUNT,
  ORDER_COMPETITIONS,
  ORDER_CONFIRMATION,
  ORDER_SCREEN,
  PAYMENT_METHOD,
  PRIVACY_POLICY,
  PROCESSING_ORDER,
  PROMOTION_NAME,
  PROMOTIONS_CURRENT,
  PROMOTIONS_MAIN,
  PROMOTIONS_WINNERS,
  RESET_PASSWORD,
  RESPONSIBLE_SERVICE,
  RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS,
  RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST,
  RETURNS_AND_EXCHANGES,
  SEARCH,
  SELF_EXCLUSION,
  SHOP_ALL_NAME,
  STORE_DETAILS,
  STORE_NAME,
  STORE_NAME_CARD,
  STORE_NAME_CHECKOUT,
  STORE_SELECTION,
  SUB_DEPARTMENT_NAME,
  SUBURB_SELECTION,
  TERMS_AND_CONDITIONS,
  VIEW_ORDER,
} from '../screens/constants';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import ViewStyled from '../styled-components/ViewStyled';
import rewardsTheme from '../theme/rewardsTheme';
import {OrderLine} from '../transport/apiServiceResponse';
import {departmentDisplayName} from '../utils/departmentDisplayName';
import {toAUD} from '../utils/functions';
import {navigateToLiquorShop} from '../utils/giftCards';
import {getLinkTrail, goBackThroughLinkTrail} from '../utils/navigation';
import {openDepartment} from '../utils/openDepartment';
import {brandRewardsCopy} from '../utils/rewards';

import CartCountBadge from './cart/CartCountBadge';
import StoreName from './checkout/StoreName';
import {isGlobalAlertBannerVisible} from './GlobalAlert/isGlobalAlertBannerVisible';
import {AccountMenuItem} from './SiteHeader/AccountMenu';
import {isSiteHeaderActive} from './SiteHeader/headerDenyList';
import {Availability} from './SiteHeader/headerTypes';
import {joinTitle} from './SiteHeader/menu/departmentJoins';
import type {MenuAction} from './SiteHeader/menu/menuTypes';
import {useMegaMenus} from './SiteHeader/menu/useMegaMenus';
import Pressable from './ui-controls/PressableWithOpacityFeedback';
import Text from './ui-controls/Text';
import {Icon} from './Icon';
import {SiteHeader} from './SiteHeader';
import StoreNameCard from './StoreNameCard';

type LiquorShopNavigation = Parameters<typeof navigateToLiquorShop>[0];

export type BrandHeaderProps = {
  navigation: BottomTabHeaderProps['navigation'] | StackHeaderProps['navigation'];
  route: BottomTabHeaderProps['route'];
  config: HeaderConfig;
};

// Stable reference for the inactive path, where the hook ignores `denied`.
const NO_DENIED: never[] = [];

const BrandHeader = observer(({navigation, route, config}: BrandHeaderProps) => {
  const stores = useStores();
  const {
    shopSelectionStore,
    rangeStore,
    feedStore,
    globalStore,
    userStore,
    cartStore,
    rewardsStore,
  } = stores;
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const isLoggedIn = userStore.isLoggedIn;
  const globalAlert = useGlobalAlert();
  // When the app-root alert strip owns the status-bar inset (native only),
  // drop BrandHeader's paddingTop so we do not double-apply safe area.
  const bannerOwnsStatusBar =
    Platform.OS !== 'web' && isGlobalAlertBannerVisible(globalAlert, globalStore.isHomeRoute);

  const isGiftCardStore = shopSelectionStore.isSelectedStoreGiftCardStore;
  // DIS-1898 (ruling C3): resolved once here, an observer, and passed down as a prop. Disabled
  // (no menus, no drift report) on native and when HEADER is denied.
  const siteHeaderActive = isSiteHeaderActive();
  const menus = useMegaMenus({
    denied: siteHeaderActive ? appConfig.WEB_HEADER_DISABLED : NO_DENIED,
    enabled: siteHeaderActive,
  });

  const handleSearchPress = () => {
    if (navigation.getId() === 'SEARCH') {
      navigation.popToTop();
    } else {
      runInAction(() => {
        globalStore.setIsFromNavigationSearch(true);
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.NEW_SEARCH,
        });
      });
    }
  };

  const handleBurgerPress = () => navigation.dispatch(DrawerActions.openDrawer());
  const handleUserPress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: isLoggedIn ? AppRoute.ACCOUNT_LOGGED_IN : AppRoute.ACCOUNT_NOT_LOGGED_IN,
    });
  const handleCartPress = () =>
    RootNavigation.navigate(AppRoute.ROOT, {
      screen: AppRoute.MAIN,
      params: {
        screen: AppRoute.CART,
      },
    });
  /** Into MAIN through the root navigator, as handleCartPress: root-flow headers sit outside it. */
  const navigateInMain = (screen: AppRoute, params?: object) =>
    RootNavigation.navigate(AppRoute.ROOT, {screen: AppRoute.MAIN, params: {screen, params}});
  // DIS-1907 header search. Every MAIN screen has `getId={() => name}` (TabNavigator.tsx), so the
  // router reuses an existing NEW_SEARCH route instead of pushing a second SearchScreen; a mounted
  // SearchScreen reads the new `keywords` from its params (forwardedKeywords, StickyFilterBar's
  // precedent). No setIsFromNavigationSearch: it clears the keywords.
  const handleSearchSubmit = (keywords: string) => navigateInMain(AppRoute.NEW_SEARCH, {keywords});
  // SearchScreen's tile press ends here when it finds no brand, promotion or article match.
  const handleSearchResultPress = (product: OrderLine) =>
    navigateInMain(AppRoute.PRODUCT, {product});
  // C4: null outside SearchProvider (root flows); openDepartment then skips the dispatch.
  const searchDispatch = useOptionalSearchDispatch();
  /** A live mega menu link (DIS-1898 B8). Promotions are read from the cache at press time. */
  const handleMenuAction = (action: MenuAction) => {
    const rawPromotions = readPromotions(stores)?.rawPromotions ?? [];
    switch (action.kind) {
      case 'department':
        // DIS-1900: Shop All skips the department's tiles. SubDepartmentScreen scopes the list.
        if (action.viewAll) {
          navigateInMain(AppRoute.SUB_DEPARTMENT, {
            department: action.department,
            subDepartment: viewAllSubDepartment(),
          });
          return;
        }
        // R57: the action goes to openDepartment unchanged, so menu and Shop tile agree.
        openDepartment(
          {
            rangeStore,
            filterStore: stores.filterStore,
            searchDispatch: searchDispatch ?? undefined,
            rawPromotions,
            navigate: navigateInMain,
          },
          action,
        );
        return;
      case 'route':
        if (action.route === AppRoute.ROOT_GIFT_CARDS_PROMOTION) {
          navigateToGiftCardsPromotionOrLoadingScreen(rawPromotions, RootNavigation.navigate, true);
        } else {
          navigateInMain(action.route, action.params);
        }
        return;
      case 'brand':
        navigateInMain(AppRoute.BRAND, {brand: action.brand});
        return;
      case 'departments':
        // `specials` stays absent unless true: React Navigation would put `?specials=false` in the URL.
        navigateInMain(AppRoute.SHOP_ALL, {
          join: action.join,
          ...(action.specials ? {specials: true} : {}),
        });
        return;
      default: {
        const unhandled: never = action;
        return unhandled;
      }
    }
  };
  const handleStoreNamePress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: AppRoute.MY_STORE,
    });
  const handleFilterIconPress = () => globalStore.setFiltersVisible(!globalStore.filtersVisible);
  const handleLogoPress = () =>
    navigation.navigate(AppRoute.MAIN, {
      screen: AppRoute.HOME,
    });

  // Universal action, navigation handles it the way it wants too...
  // Like seriously, don't bother trying to be smarter than the guys
  // who wrote the navigation library.
  const backIcon = (props?: {size?: number}) => {
    const navigationState = navigation?.getState();
    const history = navigationState?.history || [];
    const isCatalogueRoute = route?.name === AppRoute.CATALOGUE;
    // Same-route in-page link chain (article -> article). The tab history does
    // not record it — see `applyLinkTrail` in utils/navigation.
    const linkTrail = getLinkTrail(route?.params);

    if (history.length === 1 && !linkTrail && !isGiftCardStore && !isCatalogueRoute) {
      return <ShopStoreImg width={36} height={36} />;
    }

    let handleBackPress = () => {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else if (history.length > 1) {
        // Manual tab history navigation
        const previousHistoryItem = history[history.length - 2];
        const routes = navigationState?.routes || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const previousRoute = routes.find((r: any) => r.key === previousHistoryItem.key);

        if (previousRoute) {
          navigation.navigate(previousRoute.name, previousRoute.params);
        } else {
          // Fallback if history logic fails
          navigation.navigate(AppRoute.MAIN, {
            screen: AppRoute.HOME,
          });
        }
      } else {
        // No prior screen and no tab history (deep-link / web-refresh cold start).
        // Bare goBack() no-ops here and strands the user (DIS-1416). Mirror
        // handleLogoPress: route through MAIN to the store-correct home — both HOME
        // (standard stores) and GIFT_CARDS_MAIN (gift-card stores) are screens under
        // MAIN, so this resolves from the flow stacks (Checkout/Promotions/etc.) too,
        // unlike a bare navigate(homeAppRoute) which wouldn't resolve from those.
        navigation.navigate(AppRoute.MAIN, {
          screen: shopSelectionStore.homeAppRoute,
        });
      }
    };

    // Hack: Shop -> "Gift Cards" is not a real "category/department", just a
    // link to the "Gift Cards" promotion. Then, hack making "back" go to
    // "Shop", not Shop -> "Gift Cards"
    if (
      history[history.length - 1]?.key?.startsWith(AppRoute.PROMOTION) &&
      history[history.length - 2]?.key?.startsWith(AppRoute.DEPARTMENT)
    ) {
      handleBackPress = () =>
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.SHOP,
        });
    }

    // Handle deep link to offer detail with no history - go to offers list
    const routes = navigationState?.routes || [];
    const currentIndex = navigationState?.index ?? 0;
    const isOfferDetailWithNoHistory =
      route?.name === AppRoute.EXCLUSIVE_OFFER_DETAIL &&
      (currentIndex === 0 || routes.length <= 1) &&
      history.length <= 1;

    if (isOfferDetailWithNoHistory) {
      handleBackPress = () => navigation.navigate(AppRoute.EXCLUSIVE_OFFER_LIST);
    }

    // DIS-1351: CATALOGUE with nothing to go back to — deep link to
    // /catalogues, or a full page load after the Nexus catalogue download
    // navigated the browser away and the user returned. Tab history resets to
    // a single entry, which used to suppress the back affordance entirely.
    // Mirror the EXCLUSIVE_OFFER_DETAIL special-case above: keep the arrow
    // visible and send the user to Home. canGoBack() (not stack index) is the
    // discriminator because CATALOGUE is a visible tab: in tab state `routes`
    // lists every tab and `index` is the focused tab position, so the
    // stack-index checks used for EXCLUSIVE_OFFER_DETAIL can never match here.
    const isCatalogueWithNoHistory =
      isCatalogueRoute && !navigation.canGoBack() && history.length <= 1;

    if (isCatalogueWithNoHistory) {
      handleBackPress = () =>
        navigation.navigate(AppRoute.MAIN, {
          screen: AppRoute.HOME,
        });
    }

    // Last, so it wins over the special cases above: the user arrived here
    // from another instance of THIS screen via an in-page link, and that
    // origin is not in the tab history.
    if (linkTrail) {
      handleBackPress = () => goBackThroughLinkTrail(linkTrail);
    }

    if (config.back) {
      return (
        <Pressable
          onPress={handleBackPress}
          style={[styles.icon, styles.iconsLeft]}
          testID="header-back-button">
          <Icon
            name="arrow-left-long"
            size={props?.size || 16}
            color={theme.colour.background.button.primary}
          />
        </Pressable>
      );
    } else if (config.shop) {
      return <ShopStoreImg width={36} height={36} />;
    } else {
      return <View style={styles.icon} testID="header-icon" />;
    }
  };

  const filterIcon = () => {
    let showFilter = !!config.filter;

    // On DEPARTMENT screens, only show filter when products are displayed (no sub-department tiles)
    if (showFilter && config.source === DEPARTMENT_NAME) {
      const subDepts = rangeStore.getSubDepartments(rangeStore.selectedDepartment?.id);
      showFilter = subDepts.length === 0;
    }

    // Hide filter icon on product list screens — the StickyFilterBar replaces it
    if (config.source === DEPARTMENT_NAME || config.source === SUB_DEPARTMENT_NAME) {
      showFilter = false;
    }

    return showFilter ? (
      <Pressable onPress={handleFilterIconPress} style={styles.icon}>
        <FilterIconImg width={24} height={24} style={styles.filterIcon} />
      </Pressable>
    ) : (
      <View style={styles.icon} />
    );
  };

  // QA 2026-10-21 (user): on web the route row is the screen-title row ("<- Title"). The header
  // strap already shows the store, so a STORE_NAME route shows its screen title there instead.
  // Native keeps the store: its header has no strap.
  const storeNameRouteTitle = (): string => {
    switch (route?.name) {
      case AppRoute.MY_STORE:
        return 'MY STORE';
      case AppRoute.WISHLIST:
        return 'WISH LIST';
      case AppRoute.REWARDS:
        return brandRewardsCopy.menuTitle.toUpperCase();
      case AppRoute.SHOP:
        return 'SHOP';
      case AppRoute.CATALOGUE:
        return 'CATALOGUE';
      case AppRoute.CART_ORDER_CHECKOUT:
        return 'CHECKOUT';
      default:
        return '';
    }
  };

  const headerText = () => {
    let text;
    switch (config.source) {
      case DISCOVER_SEGMENT:
        text = route.params?.article?.segment || '';
        break;
      case DISCOVER_FILTER:
        // Unfiltered discover is the ALL segment. The footer calls that page Inspiration.
        text =
          feedStore.selectedSegment.value === 'ALL'
            ? 'INSPIRATION'
            : feedStore.selectedSegment.label;
        break;
      case FEED_SEGMENT:
        text = feedStore.feedDetail?.segment || route.params.article?.segment || '';
        break;
      case DEPARTMENT_NAME:
        text = rangeStore.selectedDepartment?.name?.toUpperCase() || '';
        break;
      case PROMOTION_NAME:
        text = brandRewardsCopy.promotionsTitle.toUpperCase();
        break;
      case EXCLUSIVE_OFFER_NAME:
        text = 'EXCLUSIVE OFFER';
        break;
      case SUB_DEPARTMENT_NAME:
        {
          // A View All list selects no sub-department, so the store would show the previous one.
          const {department, subDepartment}: {department?: Department; subDepartment?: Department} =
            route?.params ?? {};
          text = isViewAll(department, subDepartment)
            ? `SHOP ALL ${departmentDisplayName(department?.name ?? '').toUpperCase()}`.trim()
            : departmentDisplayName(rangeStore.selectedSubDepartment?.name ?? '').toUpperCase();
        }
        break;
      case STORE_DETAILS:
        text = 'MY STORE';
        break;
      case SUBURB_SELECTION:
        text = 'WELCOME';
        break;
      case STORE_SELECTION:
        text = 'SELECT YOUR STORE';
        break;
      case FAVOURITE_BRANDS:
        text = 'FAVOURITE BRANDS';
        break;
      case SHOP_ALL_NAME:
        {
          const {join, specials} = (route?.params ?? {}) as ShopAllParams;
          text = join ? joinTitle(join, specials).toUpperCase() : '';
        }
        break;
      case BRAND_NAME:
        text = route.params.brand?.brand?.toUpperCase() || '';
        break;
      case SEARCH:
        text = 'SEARCH';
        break;
      case LOGIN:
        text = 'LOG IN';
        break;
      case LOGIN_ENTRY:
        text = 'ENTER EMAIL';
        break;
      case NEW_ACCOUNT:
        text = 'CREATE ACCOUNT';
        break;
      case DELETE_ACCOUNT:
        text = 'DELETE ACCOUNT';
        break;
      case EDIT_ACCOUNT:
        text = 'YOUR DETAILS';
        break;
      case RESET_PASSWORD:
        text = 'RESET PASSWORD';
        break;
      case CHANGE_PASSWORD:
        text = 'CHANGE PASSWORD';
        break;
      case ACCOUNT_COMMUNICATIONS_PREFERENCES:
        text = 'PREFERENCES';
        break;
      case INFORMATION:
        text = 'HELP';
        break;
      case EXCLUSIVE_OFFER_LIST:
        text = brandRewardsCopy.exclusiveOffersTitle.toUpperCase();
        break;
      case MEMBERS_PRICE_LIST:
        text =
          route?.params?.external_sell_type === 'just-for-you'
            ? brandRewardsCopy.memberPrivatePricingTitle.toUpperCase()
            : brandRewardsCopy.memberPublicPricingTitle.toUpperCase();
        break;
      case INFORMATION_CONTENT: {
        switch (route?.params?.target) {
          case ABOUT_US:
          case InformationContentType.ABOUT_US:
            text = 'ABOUT US';
            break;
          case RETURNS_AND_EXCHANGES:
            text = 'RETURNS & EXCHANGES';
            break;
          case DELIVERY_AND_PICKUP_FAQS:
            text = 'DELIVERY & PICKUP FAQs';
            break;
          case TERMS_AND_CONDITIONS:
            text = 'TERMS & CONDITIONS';
            break;
          case PRIVACY_POLICY:
            text = 'PRIVACY POLICY';
            break;
          default:
            text = 'HELP';
        }
        break;
      }
      case CONTACT_US:
        text = 'CONTACT US';
        break;
      case CONTACT_US_SUBMIT_TICKET:
        text = 'SUBMIT A TICKET';
        break;
      case SELF_EXCLUSION:
        text = 'SELF EXCLUSION';
        break;
      case ORDER_SCREEN:
        text = 'PURCHASE HISTORY';
        break;
      case ACCOUNT_ACTION_CHOOSER:
        text = 'ACCOUNT MANAGEMENT';
        break;
      case VIEW_ORDER:
        text = route.params?.order ? `Order #${route.params?.order?.order_number}` : 'VIEW ORDER';
        break;
      case PAYMENT_METHOD:
        text = 'PAYMENT METHOD';
        break;
      case PROCESSING_ORDER:
        text = 'PROCESSING ORDER';
        break;
      case ORDER_COMPETITIONS:
        text = 'COMPETITION ENTRIES';
        break;
      case ORDER_CONFIRMATION:
        text = 'ORDER CONFIRMATION';
        break;
      case PROMOTIONS_MAIN:
        text = brandRewardsCopy.promotionsTitle.toUpperCase();
        break;
      case PROMOTIONS_CURRENT:
        text = `CURRENT ${brandRewardsCopy.promotionsTitle.toUpperCase()}`;
        break;
      case PROMOTIONS_WINNERS:
        text = 'WINNERS!';
        break;
      case RESPONSIBLE_SERVICE:
        text = 'RESPONSIBLE SERVICE';
        break;
      case RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST:
        text = '3RD PARTY REVIEW REQUEST';
        break;
      case RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS:
        text = 'LIQUOR LICENSING LAWS';
        break;
      case GIFT_CARDS_STORE_ORDER_REVIEW:
        text = 'REVIEW YOUR ORDER';
        break;
      case GIFT_CARDS_TERMS_AND_CONDITIONS:
        text = 'GIFT CARD TERMS OF USE';
        break;
      case GIFT_CARDS_HOW_TO_REDEEM:
        text = 'HOW TO REDEEM A GIFT CARD';
        break;
      case GIFT_CARDS_OPTIONS:
        text = 'GIFT CARDS';
        break;
      case GIFT_CARDS_HOW_TO_CHECK_BALANCE:
        text = 'CHECK A GIFT CARD BALANCE';
        break;
      default:
        text = '';
    }

    if (config.source === STORE_NAME) {
      if (isGiftCardStore) {
        return (
          <View style={styles.centeredView}>
            <Text style={styles.departmentName}>GIFT CARD STORE</Text>
          </View>
        );
      } else if (siteHeaderActive) {
        return (
          <View style={styles.centeredView}>
            <Text style={styles.departmentName} numberOfLines={1} ellipsizeMode="tail">
              {storeNameRouteTitle()}
            </Text>
          </View>
        );
      } else {
        return (
          <Pressable onPress={handleStoreNamePress} style={styles.storeView}>
            <Text style={styles.locationUpper}>
              MY STORE <Icon name="chevron-down" size={6} style={styles.chevron} />
            </Text>
            <Text style={[styles.location]} numberOfLines={1} ellipsizeMode="tail">
              {shopSelectionStore.selectedStore?.name || 'Your Store'}
            </Text>
          </Pressable>
        );
      }
    } else {
      return (
        <View style={styles.centeredView}>
          <Text style={styles.departmentName} numberOfLines={1} ellipsizeMode="tail">
            {text}
          </Text>
        </View>
      );
    }
  };

  const getDynamicHeader = () => (
    <View
      testID="brand-header-dynamic-header-parent"
      style={{
        flexDirection: 'row',
        justifyContent: 'center',
        backgroundColor: 'white',
      }}>
      <View
        style={[styles.storeHeader, isWebDesktopScreen && styles.webMaxWidth]}
        testID="brand-header-dynamic-header-store">
        {backIcon()}
        {headerText()}
        {filterIcon()}
      </View>
    </View>
  );
  const showRouteRow =
    !config.blinkerMode && !config.hideDynamicHeader && config.source !== STORE_NAME_CARD;

  // DIS-1897 (spec § 7.1): web renders SiteHeader unless `HEADER` is in WEB_HEADER_DISABLED.
  // Built only on that path, so native and the denied path read no new store fields.
  const renderSiteHeader = () => {
    const hasStore = !!shopSelectionStore.selectedStore;
    const ecommerceDisabled = shopSelectionStore.ecommerceDisabled;
    // R37: full variant (menus, search) unless blinker mode, a gift-card store or a selected
    // non-ecommerce store. No store selected keeps the full variant (spec § 7.4).
    const ecom = !config.blinkerMode && !isGiftCardStore && !(hasStore && ecommerceDisabled);
    const showCart = hasStore && ecom;
    const showNonEcomNotice =
      !config.blinkerMode && hasStore && ecommerceDisabled && !isGiftCardStore;
    // DIS-2010: no online shopping, so mobile drops the menu and account. The same condition as
    // the notice today, but a separate flag: the two rules may diverge.
    const inStoreOnly = !config.blinkerMode && hasStore && ecommerceDisabled && !isGiftCardStore;
    // R36: only routes registered for the store type (routes.tsx NON_ECOM_HIDDEN_ROUTES;
    // REWARDS and WISHLIST are liquor-store routes), as WebDesktopTopNavigation guards them.
    const showWishlist = !ecommerceDisabled && !isGiftCardStore;
    const showRewards = !isGiftCardStore;
    // The rows go through navigateInMain: a root-flow screen (store selection) shows this header
    // outside the drawer, where navigation.navigate(MAIN) finds no MAIN route and does nothing.
    const navigateToRewards = () => navigateInMain(AppRoute.REWARDS);
    // DIS-1920: Catalogue, Promotions and Gift Cards moved to the footer's Discover column.
    // DIS-1953: a member's row is their brand's card name and opens the member card; a signed-in
    // non-member has no card, so the row is hidden. Membership is the user's
    // `rewardsStore.hasRewards` behind the session, the screen's own predicate (AccountRewardsScreen) and promotionAccess.ts's
    // `isLoggedIn && hasRewards`: persisted hasRewards outlives the session (DIS-1633). Not
    // shopSelectionStore.loyaltyEnabled, which is the STORE's flag (DIS-1606).
    const isCardMember = isLoggedIn && rewardsStore.hasRewards;
    const memberCardRow: AccountMenuItem[] =
      showRewards && isCardMember
        ? [
            {
              label: brandRewardsCopy.memberCardTitle,
              onPress: () => navigateInMain(AppRoute.ACCOUNT_REWARDS),
            },
          ]
        : [];
    const wishlistRow: AccountMenuItem[] = showWishlist
      ? [{label: 'Wishlist', onPress: () => navigateInMain(AppRoute.WISHLIST)}]
      : [];
    const memberItems: AccountMenuItem[] = [
      {
        label: 'Account Details',
        onPress: () => navigateInMain(AppRoute.ACCOUNT_LOGGED_IN),
      },
      ...memberCardRow,
      ...wishlistRow,
      {
        label: 'Purchase History',
        onPress: () => navigateInMain(AppRoute.ACCOUNT_RECENT_ORDERS),
      },
      // R5: Log Out last; AccountMenu draws the second divider above the last item. The
      // drawer's "Log out" handler (DrawerNavigator.tsx).
      {label: 'Log Out', onPress: () => handleLogoutPress(navigation, userStore)},
    ];
    return (
      <SiteHeader
        variant={ecom ? 'ecom' : 'nonEcom'}
        storeName={shopSelectionStore.selectedStore?.name}
        availability={availabilityFor(shopSelectionStore.canDeliver, shopSelectionStore.canPickup)}
        cartCount={cartStore.getTotalOrderCount()}
        cartTotal={toAUD(cartStore.currentOrder?.total?.cents_inc_tax, true)}
        showCart={showCart}
        showUtilities={!config.blinkerMode}
        showNonEcomNotice={showNonEcomNotice}
        showMenuButton={!!config.burger}
        inStoreOnly={inStoreOnly}
        giftCardStore={isGiftCardStore}
        showStoreSelector={!config.blinkerMode}
        showSearch={config.search !== false}
        denied={appConfig.WEB_HEADER_DISABLED}
        menus={menus}
        onMenuAction={handleMenuAction}
        account={{
          isLoggedIn,
          firstName: (isLoggedIn && userStore.firstName) || undefined,
          initials: initialsFor(userStore.firstName, userStore.lastName),
          items: memberItems,
          // DIS-2043: handleUserPress's route, through the root navigator for root-flow headers.
          onGuestPress: () => navigateInMain(AppRoute.ACCOUNT_NOT_LOGGED_IN),
          onBannerPress: showRewards ? navigateToRewards : undefined,
        }}
        actions={{
          onLogoPress: handleLogoPress,
          onSearchPress: handleSearchPress,
          onSearchSubmit: handleSearchSubmit,
          onSearchResultPress: handleSearchResultPress,
          onStorePress: handleStoreNamePress,
          onCartPress: handleCartPress,
          // The drawer's condition (DrawerNavigator.tsx "Go back to the store"): gift cards
          // enabled and a gift-card store selected. Same handler.
          onBackToStorePress:
            appConfig.FF_IS_GIFT_CARDS_ENABLED && isGiftCardStore
              ? () => navigateToLiquorShop(navigation as unknown as LiquorShopNavigation, stores)
              : undefined,
        }}
        // R40 superseded 2026-10-21: the header row itself shows the store name and
        // pickup/delivery, so a STORE_NAME_CARD route renders no store-name card here
        // (showRouteRow is already false for it). The legacy path still renders StoreNameCard.
        // A STORE_NAME route with no title and no back arrow would be an empty band.
        routeRow={
          showRouteRow &&
          !(
            config.source === STORE_NAME &&
            !isGiftCardStore &&
            !config.back &&
            !storeNameRouteTitle()
          )
            ? getDynamicHeader()
            : null
        }
      />
    );
  };

  const storeNameCard = (
    <ThemeProvider theme={rewardsTheme}>
      <StoreNameCard
        onStoreNamePress={handleStoreNamePress}
        storeName={shopSelectionStore.selectedStore?.name || 'Your Store'}
        canDeliver={shopSelectionStore.canDeliver}
        canPickup={shopSelectionStore.canPickup}
      />
    </ThemeProvider>
  );
  // Checkout routes are static and blinker mode, so the header never compacts there: the
  // checkout store name stays below the header on both paths.
  const storeNameCheckout = config.source === STORE_NAME_CHECKOUT && (
    <ThemeProvider theme={rewardsTheme}>
      <StoreName
        icon={backIcon({back: true, size: 20})}
        storeName={shopSelectionStore.selectedStore?.name || 'Your Store'}
      />
    </ThemeProvider>
  );

  const insets = useSafeAreaInsets();
  const topInset = bannerOwnsStatusBar ? 0 : insets.top;

  if (siteHeaderActive) {
    // R40: no ground over SiteHeader's box. Each SiteHeader row paints its own, so a row that
    // compact hides leaves a transparent band through which the page shows and takes presses.
    // Only a non-zero top inset keeps today's ground.
    return (
      <>
        <View testID="brand-header" pointerEvents="box-none">
          {topInset > 0 ? (
            <View
              testID="brand-header-inset-ground"
              style={[styles.insetGround, {height: topInset}]}
            />
          ) : null}
          {renderSiteHeader()}
        </View>
        {storeNameCheckout}
      </>
    );
  }

  return (
    <>
      <ViewStyled testID="brand-header" style={{paddingTop: topInset}}>
        <>
          <View
            style={{flexDirection: 'row', justifyContent: 'center'}}
            testID="brand-header-outer-container">
            <View
              style={[styles.primary, isWebDesktopScreen && styles.webMaxWidth]}
              testID="brand-header-inner-container">
              <View style={styles.iconsLeft} testID="brand-header-icon-left">
                {!!config.burger && (
                  <Pressable
                    hitSlop={50}
                    onPress={handleBurgerPress}
                    testID={`hamburgler-${route.name}`}>
                    <Icon name="bars" size={20} style={styles.icons} />
                  </Pressable>
                )}
              </View>
              {isWebDesktopScreen ? (
                <Pressable onPress={handleLogoPress} style={styles.logoContainerWebDesktop}>
                  <Image source={logoImg} style={styles.logoImageWebDesktop} resizeMode="contain" />
                </Pressable>
              ) : (
                <Image source={logoImg} style={styles.logoContainer} resizeMode="contain" />
              )}

              <View style={styles.iconsRight} testID="brand-header-icons-right">
                {!config.blinkerMode && (
                  <>
                    {!!isWebDesktopScreen &&
                      !!shopSelectionStore.selectedStore &&
                      !shopSelectionStore.isSelectedStoreGiftCardStore &&
                      !shopSelectionStore.ecommerceDisabled && (
                        <Pressable onPress={handleCartPress} testID="brand-header-icons-right-cart">
                          <Icon name="cart-shopping" size={20} style={styles.icons} />
                          <CartCountBadge />
                        </Pressable>
                      )}
                    <Pressable
                      onPress={handleUserPress}
                      style={styles.middleIcon}
                      accessibilityRole="button"
                      // The SVG is aria-hidden, so the button carries the name.
                      accessibilityLabel="Account"
                      testID="brand-header-icons-right-user">
                      {/* DIS-1842: an SVG, not the FA6 `user` glyph, whose Regular
                            face rendered as tofu on SipnSave web. Same glyph
                            signed in and signed out; no font dependency. */}
                      {/* The web @svgr/webpack loader runs SVGO's default
                            removeViewBox, so without this prop the 23.73x24 path
                            draws unscaled in the 20x20 box and is clipped. Native
                            keeps the file's viewBox; this one is identical. */}
                      <AccountImg
                        viewBox="0 0 23.7307 24"
                        width={ACCOUNT_ICON_SIZE}
                        height={ACCOUNT_ICON_SIZE}
                        color={theme.colour.icon.header.primary}
                        testID="brand-header-account-icon"
                        aria-hidden={true}
                      />
                    </Pressable>
                    {!!(
                      config.search &&
                      !isGiftCardStore &&
                      !shopSelectionStore.ecommerceDisabled
                    ) && (
                      <Pressable
                        onPress={handleSearchPress}
                        testID="brand-header-icons-right-search">
                        <Icon name="magnifying-glass" size={20} style={styles.icons} />
                      </Pressable>
                    )}
                  </>
                )}
              </View>
            </View>
          </View>
          {!!isWebDesktopScreen &&
            !!shopSelectionStore.selectedStore &&
            !shopSelectionStore.isSelectedStoreGiftCardStore &&
            !config.blinkerMode && (
              <WebDesktopTopNavigation
                storeLoyaltyEnabled={shopSelectionStore.loyaltyEnabled}
                ecommerceDisabled={shopSelectionStore.ecommerceDisabled}
              />
            )}
          {showRouteRow ? getDynamicHeader() : null}
        </>
      </ViewStyled>
      {config.source === STORE_NAME_CARD && storeNameCard}
      {storeNameCheckout}
    </>
  );
});

export default BrandHeader;

const availabilityFor = (canDeliver: boolean, canPickup: boolean): Availability => {
  if (canDeliver && canPickup) {
    return 'both';
  }
  if (canPickup) {
    return 'pickup';
  }
  return canDeliver ? 'delivery' : 'inStore';
};

/** R20: first letters of first and last name; undefined falls back to the account SVG. */
const initialsFor = (firstName?: string, lastName?: string): string | undefined => {
  const first = firstName?.trim().charAt(0);
  const last = lastName?.trim().charAt(0);
  return first && last ? `${first}${last}`.toUpperCase() : undefined;
};

// SHORTCUT: 20 matches the FA6 cart and search glyphs beside it (size={20}).
// The SVG is the Figma UI Library `Profile` vector (node 3-51), whose ink
// fills its box, so a 20 box gives 20 of ink. No icon-size token exists.
// Ceiling: this header icon. Upgrade path: an icon-size token under
// app/design-system/tokens/ (DIS-1737 blocks it, DIS-1735 is the epic).
const ACCOUNT_ICON_SIZE = 20;

const styles = StyleSheet.create({
  // ViewStyled's ground, kept over the status-bar inset only (R40).
  insetGround: {backgroundColor: theme.colour.background.general.primary},
  webMaxWidth: {
    maxWidth: '1320px',
  },
  primary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  logoContainer: {
    flex: 6,
    height: 44,
  },
  logoContainerWebDesktop: {
    flex: 6,
    height: 64,
    cursor: 'pointer',
  } as ViewStyle,
  logoImageWebDesktop: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  icon: {
    flex: 1,
    height: '100%',
    color: theme.colour.icon.general.primary,
  },
  iconsLeft: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
  },
  iconsRight: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flex: 1,
  },
  middleIcon: {
    marginLeft: 8,
    marginRight: 8,
  },
  icons: {
    color: theme.colour.icon.header.primary,
  },
  // Store and filter line
  storeHeader: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 4,
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'grey',
    backgroundColor: 'white',
    height: 40,
    marginHorizontal: 16, // TODO: padding on mobile
  },
  storeView: {
    flex: 8,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  centeredView: {
    flex: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationUpper: {
    fontSize: 8,
    color: 'grey',
  },
  chevron: {
    color: 'grey',
  },
  location: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  departmentName: {
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    fontSize: 20,
  },
  filterIcon: {
    marginTop: 4,
  },
});
